import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, Calculator, RotateCcw, Send, Banknote } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Toolbar } from '@/components/Toolbar'
import { DataTable } from '@/components/DataTable'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { usePayRun } from '@/features/payroll/queries'
import { api } from '@/lib/api'
import { formatPayrollMoney, downloadPayslipPdf } from '@/features/payroll/format'
import { formatDate } from '@/lib/locale'
import { useQueryClient } from '@tanstack/react-query'

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'secondary' | 'outline' | 'danger' | 'info'> = {
  draft: 'outline', calculating: 'info', review: 'warning', approved: 'info',
  posted: 'success', paid: 'success', reversed: 'secondary', failed: 'danger', cancelled: 'secondary',
}

export function PayRunDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { hasPermission, user } = useAuth()
  const { toast } = useToast()
  const qc = useQueryClient()
  const { data, isLoading, refetch, isFetching } = usePayRun(id)
  const [busy, setBusy] = useState<'calculate' | 'approve' | 'post' | 'pay' | 'reverse' | null>(null)
  const [reverseOpen, setReverseOpen] = useState(false)
  const [reverseReason, setReverseReason] = useState('')

  const run = data?.data
  const paylines = data?.paylines ?? []
  const payslips = (data?.payslips ?? []) as unknown as Array<{
    id: string; first_name: string; last_name: string; employee_number: string
    gross_minor_units: string; net_minor_units: string; currency: string
  }>

  async function onDownloadPayslip(ps: { id: string; first_name: string; last_name: string }) {
    try {
      await downloadPayslipPdf(ps.id, `${ps.first_name}-${ps.last_name}`)
    } catch (err) {
      toast({ title: 'Download failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    }
  }

  async function call(action: 'calculate' | 'approve' | 'post' | 'pay' | 'reverse', body: object = {}) {
    if (!id) return
    setBusy(action)
    try {
      const r = await api.post<{ data: { status: string } }>(`/api/payroll/runs/${id}/${action}`, body)
      const doneLabel = { calculate: 'Run calculated', approve: 'Run approved', post: 'Run posted', pay: 'Run paid', reverse: 'Run reversed' }[action]
      toast({ title: doneLabel, description: `Status: ${r.data.status}` })
      if (user?.tenantId) {
        qc.invalidateQueries({ queryKey: ['tenant', user.tenantId, 'payroll', 'pay-run', id] })
        qc.invalidateQueries({ queryKey: ['tenant', user.tenantId, 'payroll', 'pay-runs'] })
        qc.invalidateQueries({ queryKey: ['tenant', user.tenantId, 'payroll', 'dashboard'] })
      }
      await refetch()
    } catch (err) {
      toast({
        title: `${action} failed`,
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      })
    } finally {
      setBusy(null)
    }
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: 'employee_id',
        header: 'Employee',
        cell: ({ row }: { row: { original: { first_name: string; last_name: string; employee_number: string } } }) =>
          `${row.original.first_name} ${row.original.last_name} (${row.original.employee_number})`,
      },
      {
        accessorKey: 'component_slug',
        header: 'Component',
        cell: ({ row }: { row: { original: { component_slug: string } } }) => (
          <span className="capitalize">{row.original.component_slug.replace(/_/g, ' ')}</span>
        ),
      },
      {
        accessorKey: 'component_type',
        header: 'Type',
        cell: ({ row }: { row: { original: { component_type: string } } }) => (
          <Badge variant="outline">{row.original.component_type.replace(/_/g, ' ')}</Badge>
        ),
      },
      { accessorKey: 'hours', header: 'Hours' },
      { accessorKey: 'rate', header: 'Rate' },
      {
        accessorKey: 'amount_minor_units',
        header: 'Amount',
        cell: ({ row }: { row: { original: { amount_minor_units: string; currency: string } } }) => (
          <span className={Number(row.original.amount_minor_units) < 0 ? 'text-destructive' : ''}>
            {formatPayrollMoney(row.original.amount_minor_units, row.original.currency)}
          </span>
        ),
      },
    ],
    [],
  )

  if (isLoading || !run) {
    return (
      <Page title="Pay run">
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      </Page>
    )
  }

  const canCalc = hasPermission('payroll.runs.create') && ['draft', 'failed', 'calculating', 'review'].includes(run.status)
  const canApprove = hasPermission('payroll.runs.approve') && run.status === 'review'
  const canPost = hasPermission('payroll.runs.post') && run.status === 'approved'
  const canPay = hasPermission('payroll.payments.batch') && run.status === 'posted'
  const canReverse = hasPermission('payroll.runs.reverse') && ['posted', 'paid'].includes(run.status)

  return (
    <Page
      title={`Pay run ${run.run_number}`}
      description={`Pay date ${formatDate(run.pay_date)} · Period ${formatDate(run.period_start)} → ${formatDate(run.period_end)} · ${run.run_type.replace(/_/g, ' ')}`}
      actions={
        <Toolbar>
          <Button variant="outline" onClick={() => navigate('/payroll/runs')}>
            <ArrowLeft className="mr-2 size-4" /> Back
          </Button>
          {canCalc ? (
            <Button onClick={() => call('calculate')} disabled={busy === 'calculate' || isFetching}>
              <Calculator className="mr-2 size-4" /> {busy === 'calculate' ? 'Calculating…' : run.status === 'draft' ? 'Calculate' : 'Recalculate'}
            </Button>
          ) : null}
          {canApprove ? (
            <Button onClick={() => call('approve')} disabled={busy === 'approve'}>
              <CheckCircle2 className="mr-2 size-4" /> {busy === 'approve' ? 'Approving…' : 'Approve'}
            </Button>
          ) : null}
          {canPost ? (
            <Button onClick={() => call('post')} disabled={busy === 'post'}>
              <Send className="mr-2 size-4" /> {busy === 'post' ? 'Posting…' : 'Post to GL'}
            </Button>
          ) : null}
          {canPay ? (
            <Button onClick={() => call('pay')} disabled={busy === 'pay'}>
              <Banknote className="mr-2 size-4" /> {busy === 'pay' ? 'Paying…' : 'Pay (simulate ACH)'}
            </Button>
          ) : null}
          {canReverse ? (
            <Button variant="danger" onClick={() => setReverseOpen(true)} disabled={busy === 'reverse'}>
              <RotateCcw className="mr-2 size-4" /> Reverse
            </Button>
          ) : null}
        </Toolbar>
      }
    >
      <Card>
        <CardContent className="grid gap-4 p-6 sm:grid-cols-4">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Status</div>
            <Badge variant={STATUS_VARIANT[run.status] || 'secondary'} className="mt-1">{run.status}</Badge>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Employees</div>
            <div className="mt-1 text-lg font-semibold">{run.employee_count}</div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Gross</div>
            <div className="mt-1 text-lg font-semibold">{formatPayrollMoney(run.gross_minor_units, run.currency)}</div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Net</div>
            <div className="mt-1 text-lg font-semibold text-emerald-600">{formatPayrollMoney(run.net_minor_units, run.currency)}</div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Employee tax</div>
            <div className="mt-1 text-base font-medium">{formatPayrollMoney(run.employee_tax_minor_units, run.currency)}</div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Employer tax</div>
            <div className="mt-1 text-base font-medium">{formatPayrollMoney(run.employer_tax_minor_units, run.currency)}</div>
          </div>
        </CardContent>
      </Card>

      {run.status === 'draft' ? (
        <Card>
          <CardContent className="space-y-2 p-5">
            <h2 className="text-base font-semibold">Run hasn&apos;t been calculated yet</h2>
            <p className="text-sm text-muted-foreground">
              Click <strong>Calculate</strong> to load active employees + pay components, walk the formula DAG, write the calculation snapshot, and move the run into review.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {paylines.length === 0 && run.status !== 'draft' ? (
        <Card>
          <CardContent className="space-y-2 p-5">
            <p className="text-sm text-muted-foreground">No paylines on this run. Recalculate to populate.</p>
          </CardContent>
        </Card>
      ) : paylines.length > 0 ? (
        <Card>
          <CardContent className="space-y-3 p-6">
            <h2 className="text-base font-semibold">Paylines</h2>
            <DataTable columns={columns as never} rows={paylines} />
          </CardContent>
        </Card>
      ) : null}

      {payslips.length > 0 ? (
        <Card>
          <CardContent className="space-y-3 p-6">
            <h2 className="text-base font-semibold">Payslips</h2>
            <div className="space-y-1">
              {payslips.map((ps) => (
                <div key={ps.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-border px-3 py-2">
                  <div className="text-sm">
                    <span className="font-medium">{ps.first_name} {ps.last_name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{ps.employee_number}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground">Net {formatPayrollMoney(ps.net_minor_units, ps.currency)}</span>
                    <Button size="sm" variant="ghost" onClick={() => onDownloadPayslip(ps)}>
                      <Send className="mr-2 size-4" /> Payslip PDF
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Modal
        open={reverseOpen}
        onOpenChange={setReverseOpen}
        title={`Reverse pay run ${run.run_number}?`}
        footer={
          <>
            <Button variant="outline" onClick={() => setReverseOpen(false)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={async () => {
                await call('reverse', { reason: reverseReason })
                setReverseOpen(false)
                setReverseReason('')
              }}
              disabled={busy === 'reverse'}
            >
              {busy === 'reverse' ? 'Reversing…' : 'Reverse'}
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-sm">
          <p>This creates offsetting GL entries (debits become credits, credits become debits) so the ledger reflects a zero net effect.</p>
          <FormField
            label="Reason"
            placeholder="e.g. Wrong period selected; re-running with correct dates"
            value={reverseReason}
            onChange={(e) => setReverseReason(e.target.value)}
          />
        </div>
      </Modal>
    </Page>
  )
}
